import { useEffect, useRef, useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { loadGoogleMaps } from "../lib/maps";
import { zctasInPolygon, zctasByCodes } from "../lib/zcta";
import { DAYS, DAY_COLORS } from "../lib/serviceArea";

// Draw a freehand perimeter on the map → pull every ZIP (ZCTA) that's inside or
// touches it (Census TIGERweb) → assign the batch to a service day → save to
// service_area. Complements the manual zip-by-zip list in ServiceAreaEditor.
//
// The map also SHOWS the saved service area: every serviced ZIP drawn in its day's
// color, auto-fitted so the whole area is visible on open, with the ZIP number
// labeled over each shape once you're zoomed in close enough to read them.
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Below this zoom the labels would overlap into noise; above it each ZCTA is big
// enough on screen to carry its own 5 digits.
const LABEL_ZOOM = 11;

// Tall enough that fitBounds lands on zoom 9 for a Philly-metro-sized area instead
// of snapping down to 8 — at 440px the whole-area fit was marginal by ~10px and the
// integer-zoom snap made the highlight look postage-stamp small.
const MAP_H = 520;
// Phones: 520px is nearly the whole viewport — a shorter map leaves the controls
// and the found-ZIP list reachable without a long scroll. fitToServiced reads the
// real offsetHeight, so the auto-fit still lands correctly.
const MAP_H_MOBILE = 400;

// zip -> paths, surviving tab switches/remounts. Boundaries never change within a
// session, and refetching ~135 shapes from Census on every Settings visit is rude.
const boundaryCache = new Map();

// Area-weighted centroid (shoelace) of the polygon's largest ring — where the ZIP
// label sits. A plain vertex average drifts badly on L-shaped river-boundary zips.
function centroidOf(paths) {
  let best = null, bestArea = 0;
  for (const ring of paths || []) {
    if (!ring || ring.length < 3) continue;
    let a = 0, cx = 0, cy = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const f = ring[j].lng * ring[i].lat - ring[i].lng * ring[j].lat;
      a += f; cx += (ring[j].lng + ring[i].lng) * f; cy += (ring[j].lat + ring[i].lat) * f;
    }
    if (Math.abs(a) > Math.abs(bestArea)) { bestArea = a; best = { lat: cy / (3 * a), lng: cx / (3 * a) }; }
  }
  if (best && Number.isFinite(best.lat) && Number.isFinite(best.lng)) return best;
  const r0 = (paths && paths[0]) || [];
  if (!r0.length) return null;
  return { lat: r0.reduce((t, p) => t + p.lat, 0) / r0.length, lng: r0.reduce((t, p) => t + p.lng, 0) / r0.length };
}

export function ServiceAreaMap({ serviceArea, clients = [], onRefresh, showToast }) {
  const isMobile = useIsMobile();
  // Phones: "greedy" let a one-finger scroll over a 400px map pan the map instead of
  // the page (scroll trap). "cooperative" scrolls the page with one finger and pans
  // the map with two — Google shows its own "use two fingers" hint.
  const gesture = isMobile ? "cooperative" : "greedy";
  const mapH = isMobile ? MAP_H_MOBILE : MAP_H;
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const wrapRef = useRef(null);
  const tipRef = useRef(null);
  const tipTimer = useRef(null);     // auto-hide for tap-revealed tips (touch has no mouseout)
  const captureRef = useRef(null);   // transparent pointer-capture layer over the map while drawing
  const projRef = useRef(null);      // OverlayView, for pixel→latLng during a freehand drag
  const draw = useRef({ active: false, path: [], poly: null, lasso: null, listeners: [] });
  const zctaLayer = useRef({}); // zip -> google.maps.Polygon (lasso PREVIEW layer)
  const servicedLayer = useRef({}); // zip -> { poly, marker, day } (SAVED service area)
  const servicedSeq = useRef(0);    // stale-render guard for the async boundary fetch
  const didFitServiced = useRef(false);
  const [servicedLoading, setServicedLoading] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState([]); // [{ zip, existingDay }]
  const [day, setDay] = useState("saturday");
  const [saving, setSaving] = useState(false);

  const existingDayOf = (zip) => {
    for (const [d, zips] of Object.entries(serviceArea || {})) if ((zips || []).includes(zip)) return d;
    return null;
  };

  // load + init map (mirrors ClientMap)
  useEffect(() => { loadGoogleMaps().then(() => setMapReady(true)).catch(() => showToast && showToast("Map failed to load", "error")); }, []); // eslint-disable-line
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapObj.current) return;
    const g = window.google;
    mapObj.current = new g.maps.Map(mapRef.current, {
      zoom: 10, center: { lat: 39.9526, lng: -75.1652 },
      mapTypeControl: false, streetViewControl: false, fullscreenControl: true, gestureHandling: gesture,
      styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
    });
    const pts = (clients || []).filter((c) => Number.isFinite(+c.lat) && Number.isFinite(+c.lng));
    if (pts.length) { const b = new g.maps.LatLngBounds(); pts.forEach((c) => b.extend({ lat: +c.lat, lng: +c.lng })); mapObj.current.fitBounds(b); }
    // An empty OverlayView gives us a projection so a freehand drag (mouse OR touch)
    // can convert screen pixels → lat/lng ourselves — no reliance on Google's
    // mouse-only map events, which don't fire for touch and let the page scroll.
    const ov = new g.maps.OverlayView();
    ov.onAdd = () => {}; ov.draw = () => {}; ov.onRemove = () => {};
    ov.setMap(mapObj.current);
    projRef.current = ov;
    // ZIP labels only exist once you can read them — toggled per zoom change.
    mapObj.current.addListener("zoom_changed", () => {
      const show = (mapObj.current.getZoom() || 0) >= LABEL_ZOOM;
      Object.values(servicedLayer.current).forEach((e) => e.marker && e.marker.setVisible(show));
    });
    if (!document.getElementById("scoop-zcta-label-css")) {
      const st = document.createElement("style");
      st.id = "scoop-zcta-label-css";
      // White halo so the digits stay readable over any day color or map detail.
      st.textContent = ".scoop-zcta-label{text-shadow:0 0 3px #fff,0 0 5px #fff,0 1px 2px rgba(255,255,255,.95)}";
      document.head.appendChild(st);
    }
  }, [mapReady]); // eslint-disable-line

  // Rotating a phone past the breakpoint (or a resize) flips the gesture mode live.
  useEffect(() => {
    const map = mapObj.current;
    if (!map || draw.current.poly || draw.current.active) return; // never override the frozen map mid-draw
    map.setOptions({ gestureHandling: gesture });
  }, [gesture, mapReady]);

  // ── Saved service area: highlight every serviced ZIP in its day color ──
  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    const seq = ++servicedSeq.current;
    const g = window.google, map = mapObj.current;
    const dayOf = {};
    for (const [d, zips] of Object.entries(serviceArea || {})) (zips || []).forEach((z) => { dayOf[z] = d; });

    // Drop zips no longer serviced; recolor ones whose day moved.
    for (const [zip, entry] of Object.entries(servicedLayer.current)) {
      if (!dayOf[zip]) {
        entry.poly.setMap(null); entry.marker && entry.marker.setMap(null);
        delete servicedLayer.current[zip];
      } else if (entry.day !== dayOf[zip]) {
        const col = DAY_COLORS[dayOf[zip]] || "#666";
        entry.poly.setOptions({ strokeColor: col, fillColor: col });
        entry.day = dayOf[zip];
      }
    }

    const missing = Object.keys(dayOf).filter((z) => !servicedLayer.current[z]);
    const draw = (zip, paths) => {
      if (servicedLayer.current[zip] || !paths) return;
      const day = dayOf[zip]; if (!day) return; // serviceArea changed while fetching
      const col = DAY_COLORS[day] || "#666";
      const poly = new g.maps.Polygon({
        map, paths, zIndex: 1, // under the lasso-preview layer
        strokeColor: col, strokeOpacity: .75, strokeWeight: 1.2, fillColor: col, fillOpacity: .18, clickable: true,
      });
      poly.addListener("mouseover", () => poly.setOptions({ fillOpacity: .34 }));
      poly.addListener("mousemove", (e) => showTip(`${zip} · ${cap(servicedLayer.current[zip]?.day || day)}`, e.domEvent));
      poly.addListener("mouseout", () => { poly.setOptions({ fillOpacity: .18 }); hideTip(); });
      // Touch has no hover: a tap reveals the ZIP · day for a moment (labels only exist at zoom ≥ 11).
      poly.addListener("click", (e) => { if (window.matchMedia("(hover:none)").matches) showTipAt(`${zip} · ${cap(servicedLayer.current[zip]?.day || day)}`, e, true); });
      const c = centroidOf(paths);
      const marker = c ? new g.maps.Marker({
        map, position: c, clickable: false, zIndex: 2,
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 0 }, // label only, no pin
        label: { text: zip, fontSize: "11px", fontWeight: "700", color: "#1d1d1f", className: "scoop-zcta-label" },
        visible: (map.getZoom() || 0) >= LABEL_ZOOM,
      }) : null;
      servicedLayer.current[zip] = { poly, marker, day };
    };

    const fitToServiced = () => {
      if (didFitServiced.current) return; // only on first paint — never yank the map mid-session
      const entries = Object.values(servicedLayer.current);
      if (!entries.length) return;
      // Fit computed by hand with setZoom/setCenter instead of map.fitBounds():
      // fitBounds issued from this callback is silently ignored in some contexts
      // (observed: correct bounds in, view untouched), while setZoom/setCenter
      // always apply — and the math is small.
      let latMin = 90, latMax = -90, lngMin = 180, lngMax = -180;
      entries.forEach((e) => e.poly.getPaths().forEach((ring) => ring.forEach((pt) => {
        const la = pt.lat(), ln = pt.lng();
        if (la < latMin) latMin = la; if (la > latMax) latMax = la;
        if (ln < lngMin) lngMin = ln; if (ln > lngMax) lngMax = ln;
      })));
      if (latMin > latMax) return;
      const div = map.getDiv();
      const w = div.offsetWidth || 600, h = div.offsetHeight || MAP_H;
      // Mercator vertical fraction of the world between two latitudes.
      const mercY = (lat) => {
        const r = Math.max(-85, Math.min(85, lat)) * Math.PI / 180;
        return (1 - Math.log(Math.tan(Math.PI / 4 + r / 2)) / Math.PI) / 2;
      };
      const yTop = mercY(latMax), yBot = mercY(latMin);
      const fracX = Math.max((lngMax - lngMin) / 360, 1e-6);
      const fracY = Math.max(yBot - yTop, 1e-6);
      const PAD = 24; // px each side
      const zoom = Math.max(3, Math.min(15, Math.floor(Math.log2(Math.min(
        (w - 2 * PAD) / (256 * fracX),
        (h - 2 * PAD) / (256 * fracY),
      )))));
      // Center: lng midpoint; lat from the Mercator midpoint so tall areas center visually.
      const yMid = (yTop + yBot) / 2;
      const latCenter = (Math.atan(Math.exp((1 - 2 * yMid) * Math.PI)) * 2 - Math.PI / 2) * 180 / Math.PI;
      map.setCenter({ lat: latCenter, lng: (lngMin + lngMax) / 2 });
      map.setZoom(zoom);
      didFitServiced.current = true;
    };

    const cached = missing.filter((z) => boundaryCache.has(z));
    cached.forEach((z) => draw(z, boundaryCache.get(z)));
    const toFetch = missing.filter((z) => !boundaryCache.has(z));
    if (!toFetch.length) { fitToServiced(); return; }
    setServicedLoading(true);
    zctasByCodes(toFetch)
      .then((zs) => {
        zs.forEach((z) => boundaryCache.set(z.zip, z.paths));
        if (servicedSeq.current !== seq) return; // superseded render
        toFetch.forEach((z) => boundaryCache.has(z) && draw(z, boundaryCache.get(z)));
        fitToServiced();
      })
      .catch(() => { /* map stays usable without the highlight layer */ })
      .finally(() => { if (servicedSeq.current === seq) setServicedLoading(false); });
  }, [mapReady, serviceArea]); // eslint-disable-line

  function clearResults() {
    Object.values(zctaLayer.current).forEach((p) => p.setMap(null)); zctaLayer.current = {};
    if (draw.current.lasso) { draw.current.lasso.setMap(null); draw.current.lasso = null; }
    hideTip(); setFound([]);
  }

  // floating ZIP label that follows the cursor over a highlighted ZCTA
  function showTip(zip, domEvent) {
    const wrap = wrapRef.current, tip = tipRef.current;
    if (!wrap || !tip || !domEvent) return;
    const r = wrap.getBoundingClientRect();
    placeTip(zip, domEvent.clientX - r.left + 12, domEvent.clientY - r.top + 12, false);
  }
  // Tap-revealed variant: positions from the event's latLng through the overlay
  // projection (a touch-synthesized click's domEvent has no reliable clientX/Y),
  // falling back to the DOM event; auto-hides since touch never fires mouseout.
  function showTipAt(text, e, autoHide) {
    const wrap = wrapRef.current, mapEl = mapRef.current;
    if (!wrap || !mapEl) return;
    let x = null, y = null;
    const proj = projRef.current && projRef.current.getProjection();
    if (proj && e && e.latLng) {
      const p = proj.fromLatLngToContainerPixel(e.latLng);
      if (p) { x = p.x + (mapEl.offsetLeft || 0); y = p.y + (mapEl.offsetTop || 0); }
    }
    if (x == null && e && e.domEvent && Number.isFinite(e.domEvent.clientX)) {
      const r = wrap.getBoundingClientRect();
      x = e.domEvent.clientX - r.left; y = e.domEvent.clientY - r.top;
    }
    if (x == null) return;
    // keep the label inside the map: above the finger, clamped to the wrap's width
    const tipW = 110;
    placeTip(text, Math.max(4, Math.min(x - tipW / 2, wrap.clientWidth - tipW - 4)), Math.max(4, y - 40), autoHide);
  }
  function placeTip(text, left, top, autoHide) {
    const tip = tipRef.current; if (!tip) return;
    if (tipTimer.current) { clearTimeout(tipTimer.current); tipTimer.current = null; }
    tip.textContent = text;
    tip.style.left = left + "px";
    tip.style.top = top + "px";
    tip.style.opacity = "1";
    if (autoHide) tipTimer.current = setTimeout(hideTip, 1800);
  }
  function hideTip() {
    if (tipTimer.current) { clearTimeout(tipTimer.current); tipTimer.current = null; }
    if (tipRef.current) tipRef.current.style.opacity = "0";
  }
  useEffect(() => () => { if (tipTimer.current) clearTimeout(tipTimer.current); }, []);

  // screen pixel → lat/lng via the overlay projection (relative to the map's top-left)
  function pxToLatLng(clientX, clientY) {
    const proj = projRef.current && projRef.current.getProjection();
    const mapEl = mapRef.current;
    if (!proj || !mapEl) return null;
    const r = mapEl.getBoundingClientRect();
    return proj.fromContainerPixelToLatLng(new window.google.maps.Point(clientX - r.left, clientY - r.top));
  }

  function beginLasso() {
    const map = mapObj.current; if (!map) return;
    clearResults(); setDrawing(true);
    // Freeze the map under the capture layer; the layer (touch-action:none) takes
    // all pointer input so neither the map nor the page pans while drawing.
    map.setOptions({ draggable: false, gestureHandling: "none", disableDoubleClickZoom: true, draggableCursor: "crosshair" });
    const g = window.google, st = draw.current;
    st.active = false; st.path = [];
    st.poly = new g.maps.Polyline({ map, strokeColor: "#0071E3", strokeWeight: 3, clickable: false });
  }

  // Pointer handlers on the capture layer — one code path for mouse AND touch.
  function drawDown(e) {
    const st = draw.current; if (!drawing || !st.poly) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const ll = pxToLatLng(e.clientX, e.clientY); if (!ll) return;
    st.active = true; st.path = [{ lat: ll.lat(), lng: ll.lng() }]; st.poly.setPath(st.path);
  }
  function drawMove(e) {
    const st = draw.current; if (!st.active) return;
    e.preventDefault();
    const ll = pxToLatLng(e.clientX, e.clientY); if (!ll) return;
    st.path.push({ lat: ll.lat(), lng: ll.lng() }); st.poly.setPath(st.path);
  }
  function drawUp() { if (draw.current.active) endLasso(); }

  function endLasso() {
    const g = window.google, map = mapObj.current, st = draw.current;
    st.active = false;
    map.setOptions({ draggable: true, gestureHandling: gesture, draggableCursor: null });
    setDrawing(false);
    const path = st.path.slice();
    if (st.poly) { st.poly.setMap(null); st.poly = null; }
    if (path.length < 3) return;
    st.lasso = new g.maps.Polygon({ map, paths: path, strokeColor: "#0071E3", strokeOpacity: .9, strokeWeight: 2, fillColor: "#0071E3", fillOpacity: .04, clickable: false });
    extract(path);
  }

  async function extract(path) {
    setLoading(true);
    try {
      const zs = await zctasInPolygon(path);
      const g = window.google, col = DAY_COLORS[day];
      zs.forEach((z) => {
        if (zctaLayer.current[z.zip] || !z.paths) return;
        const poly = new g.maps.Polygon({ map: mapObj.current, paths: z.paths, zIndex: 3, strokeColor: col, strokeOpacity: .85, strokeWeight: 1.5, fillColor: col, fillOpacity: .2, clickable: true });
        poly.addListener("mouseover", () => poly.setOptions({ fillOpacity: .42, strokeWeight: 2.5 }));
        poly.addListener("mousemove", (e) => showTip(z.zip, e.domEvent));
        poly.addListener("mouseout", () => { poly.setOptions({ fillOpacity: .2, strokeWeight: 1.5 }); hideTip(); });
        poly.addListener("click", (e) => { if (window.matchMedia("(hover:none)").matches) showTipAt(z.zip, e, true); });
        zctaLayer.current[z.zip] = poly;
      });
      setFound(zs.map((z) => ({ zip: z.zip, existingDay: existingDayOf(z.zip) })).sort((a, b) => a.zip.localeCompare(b.zip)));
      if (!zs.length) showToast && showToast("No ZIP codes found in that area", "error");
    } catch { showToast && showToast("Couldn't fetch ZIP codes — try again", "error"); }
    setLoading(false);
  }

  function removeZip(zip) {
    if (zctaLayer.current[zip]) { zctaLayer.current[zip].setMap(null); delete zctaLayer.current[zip]; }
    setFound((f) => f.filter((x) => x.zip !== zip));
  }

  // recolor the found ZIP fills to preview the target day
  useEffect(() => {
    const col = DAY_COLORS[day];
    Object.values(zctaLayer.current).forEach((p) => p.setOptions({ strokeColor: col, fillColor: col }));
  }, [day]);

  async function assign() {
    if (!found.length) return;
    setSaving(true);
    const zips = found.map((f) => f.zip);
    const H = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` };
    try {
      // no unique constraint on (org,zip) → clear these zips first, then insert the batch
      await fetch(`${SUPABASE_URL}/rest/v1/service_area?zip=in.(${zips.join(",")})`, { method: "DELETE", headers: H });
      await fetch(`${SUPABASE_URL}/rest/v1/service_area`, {
        method: "POST", headers: { ...H, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify(zips.map((zip) => ({ zip, day }))),
      });
      showToast && showToast(`${zips.length} ZIP${zips.length > 1 ? "s" : ""} assigned to ${cap(day)}`);
      onRefresh && onRefresh();
      clearResults();
    } catch { showToast && showToast("Error saving ZIP codes", "error"); }
    setSaving(false);
  }

  const reassigning = found.filter((f) => f.existingDay && f.existingDay !== day).length;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div className="card-title" style={{ margin: 0 }}>🗺️ Draw your service area</div>
          <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>
            {drawing ? "Press and drag on the map to lasso an area — release to finish."
                     : isMobile
                       ? "Your serviced ZIPs are highlighted by day — tap one to see its ZIP, pinch to zoom, two fingers to move the map. To add more, tap Draw and trace a shape around them."
                       : "Your serviced ZIPs are highlighted by day — zoom in to see each ZIP number. To add more, click Draw and drag a shape around them."}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!drawing && <button className="btn btn-gold btn-sm" onClick={beginLasso}>✏️ {found.length ? "Redraw" : "Draw area"}</button>}
          {drawing && <button className="btn btn-ghost btn-sm" disabled>Drawing…</button>}
          {found.length > 0 && !drawing && <button className="btn btn-ghost btn-sm" onClick={clearResults}>Clear</button>}
        </div>
      </div>

      <div ref={wrapRef} style={{ position: "relative", touchAction: drawing ? "none" : "auto" }}>
        <div ref={mapRef} style={{ width: "100%", height: mapH, borderRadius: "var(--r-sm)", border: "1px solid var(--border)", overflow: "hidden", background: "#eef1f5" }} />
        {/* Freehand capture layer — only interactive while drawing. touch-action:none
            stops the browser from scrolling/zooming the page during a finger drag. */}
        {drawing && (
          <div ref={captureRef}
            onPointerDown={drawDown} onPointerMove={drawMove} onPointerUp={drawUp} onPointerCancel={drawUp}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: mapH, zIndex: 4, cursor: "crosshair", touchAction: "none", background: "transparent" }} />
        )}
        <div ref={tipRef} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", background: "rgba(29,29,31,.92)", color: "#fff", padding: "3px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700, opacity: 0, transition: "opacity .08s", zIndex: 5, whiteSpace: "nowrap" }} />
      </div>

      {servicedLoading && <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 10 }}>⏳ Loading your service area…</div>}
      {loading && <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 10 }}>⏳ Finding ZIP codes in that area…</div>}

      {found.length > 0 && !loading && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{found.length} ZIP{found.length > 1 ? "s" : ""} in this area</span>
            <span style={{ fontSize: 12, color: "var(--text2)" }}>Assign to</span>
            <select value={day} onChange={(e) => setDay(e.target.value)} style={{ border: "1px solid var(--border)", borderRadius: 6, padding: "6px 8px", fontSize: 13, background: "var(--surface)" }}>
              {DAYS.map((d) => <option key={d} value={d}>{cap(d)}</option>)}
            </select>
            <button className="btn btn-gold btn-sm" onClick={assign} disabled={saving}>
              {saving ? "Saving…" : `Assign ${found.length} to ${cap(day)}`}
            </button>
            {reassigning > 0 && <span style={{ fontSize: 11.5, color: "#8A5A06" }}>⚠️ {reassigning} already on another day — this moves them.</span>}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {found.map((f) => (
              <span key={f.zip} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: isMobile ? 8 : 6, padding: isMobile ? "0 2px 0 10px" : "2px 6px 2px 9px", fontSize: isMobile ? 13 : 12, color: "var(--text)" }}>
                {f.zip}
                {f.existingDay && <span style={{ fontSize: 10, color: "var(--text3)" }}>· {cap(f.existingDay).slice(0, 3)}</span>}
                {/* Phones: title= never shows on touch and a 12px × isn't tappable — 32px hit area + aria-label. */}
                <button onClick={() => removeZip(f.zip)} title="Remove" aria-label={`Remove ${f.zip}`}
                  style={isMobile
                    ? { border: "none", background: "none", cursor: "pointer", color: "var(--text2)", fontSize: 18, lineHeight: 1, padding: 0, minWidth: 36, minHeight: 36, display: "inline-flex", alignItems: "center", justifyContent: "center" }
                    : { border: "none", background: "none", cursor: "pointer", color: "var(--text3)", fontSize: 14, lineHeight: 1, padding: "0 2px" }}>×</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* day legend */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
        {DAYS.map((d) => (
          <span key={d} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text2)" }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: DAY_COLORS[d], display: "inline-block" }} />{cap(d).slice(0, 3)}
          </span>
        ))}
      </div>
    </div>
  );
}
